import { useState, useEffect } from 'react';
import { Loader2, Copy, Check, Code2, Info, ChevronDown, Calendar, ExternalLink } from 'lucide-react';
import { usePricingSaasData, type PricingVersion } from '../hooks/usePricingSaasData';
import { PageSelector } from './PageSelector';
import { PricingCardsDisplay } from './PricingCardsDisplay';
import { ErrorState } from './ErrorState';
import { RunExtractButton } from './RunExtractButton';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const MANAGEMENT_API_BASE = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_URL;
const API_KEY = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY;

interface VersionFile {
  id: string;
  version: string;
  screenshot_url: string | null;
  ocr_url: string | null;
  markdown_url: string | null;
  html_url: string | null;
}

// Helper to convert version string to a readable date
// Supports: "20251211" => Dec 11, 2025, or "2025W51" => week range
function versionToDate(version: string): string | null {
  // Try YYYYMMDD format first (e.g., "20251211")
  const dateMatch = version.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (dateMatch) {
    const year = parseInt(dateMatch[1], 10);
    const month = parseInt(dateMatch[2], 10) - 1; // 0-indexed
    const day = parseInt(dateMatch[3], 10);
    const date = new Date(Date.UTC(year, month, day));
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  // Try week format (e.g., "2025W51")
  const weekMatch = version.match(/^(\d{4})W(\d{1,2})$/);
  if (weekMatch) {
    const year = parseInt(weekMatch[1], 10);
    const week = parseInt(weekMatch[2], 10);

    // ISO week date calculation
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const dayOfWeek = jan4.getUTCDay() || 7;
    const week1Start = new Date(jan4);
    week1Start.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);

    const weekStart = new Date(week1Start);
    weekStart.setUTCDate(week1Start.getUTCDate() + (week - 1) * 7);

    const weekEnd = new Date(weekStart);
    weekEnd.setUTCDate(weekStart.getUTCDate() + 6);

    const formatDate = (d: Date) => d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    return `Week of ${formatDate(weekStart)} - ${formatDate(weekEnd)}`;
  }

  return null;
}

// Format version info for display
function formatVersionInfo(version: string): string {
  const dateStr = versionToDate(version);
  if (dateStr) {
    return `Version: ${version} (${dateStr})`;
  }
  return `Version: ${version}`;
}

interface PlansSectionProps {
  slug: string;
}

// JSON Viewer Modal Component with syntax highlighting
interface JsonViewerModalProps {
  data: unknown;
  title?: string;
}

const JsonViewerModal: React.FC<JsonViewerModalProps> = ({ data, title = 'Raw JSON' }) => {
  const [copied, setCopied] = useState(false);

  const jsonString = JSON.stringify(data, null, 2);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Simple syntax highlighting
  const highlightJson = (json: string) => {
    return json
      .replace(/(".*?")\s*:/g, '<span class="text-purple-400">$1</span>:') // keys
      .replace(/:\s*(".*?")/g, ': <span class="text-green-400">$1</span>') // string values
      .replace(/:\s*(\d+\.?\d*)/g, ': <span class="text-blue-400">$1</span>') // numbers
      .replace(/:\s*(true|false)/g, ': <span class="text-amber-400">$1</span>') // booleans
      .replace(/:\s*(null)/g, ': <span class="text-gray-500">$1</span>'); // null
  };

  return (
    <Dialog>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                <Code2 className="h-4 w-4" />
              </Button>
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>View raw JSON</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{title}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className="h-8"
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-500 mr-1.5" />
              ) : (
                <Copy className="h-4 w-4 mr-1.5" />
              )}
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
          <div className="max-h-[60vh] overflow-auto">
            <pre
              className="p-4 text-xs font-mono text-gray-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: highlightJson(jsonString) }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Helper to format version for display in dropdown
function formatVersionLabel(version: PricingVersion): string {
  // Prefer quarter, then week, then month, then raw version
  let label = '';

  if (version.quarter) {
    const match = version.quarter.match(/^(\d{4})Q(\d)$/);
    if (match) {
      label = `Q${match[2]} ${match[1]}`;
    } else {
      label = version.quarter;
    }
  } else if (version.week) {
    const match = version.week.match(/^(\d{4})W(\d+)$/);
    if (match) {
      label = `Week ${match[2]}, ${match[1]}`;
    } else {
      label = version.week;
    }
  } else if (version.month) {
    label = version.month;
  } else {
    // Fall back to version date
    const dateStr = versionToDate(version.version);
    label = dateStr || version.version;
  }

  // Always append raw version in brackets
  return `${label} (${version.version})`;
}

export const PlansSection: React.FC<PlansSectionProps> = ({ slug }) => {
  const { toast } = useToast();
  const [screenshotFiles, setScreenshotFiles] = useState<VersionFile[]>([]);
  const [screenshotsLoading, setScreenshotsLoading] = useState<boolean>(true);

  const {
    data,
    loading,
    error,
    selectedPageIndex,
    setSelectedPageIndex,
    refetch,
    versions,
    selectedVersion,
    setSelectedVersion,
    versionsLoading
  } = usePricingSaasData(slug);

  // Fetch screenshot files from Management API
  useEffect(() => {
    const fetchScreenshotFiles = async () => {
      if (!slug) {
        setScreenshotsLoading(false);
        return;
      }

      setScreenshotsLoading(true);
      try {
        const pageUrl = `${MANAGEMENT_API_BASE}/pages/slug/${slug}`;
        const response = await fetch(pageUrl, {
          headers: {
            'X-API-Key': API_KEY,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.error('[PlansSection] Failed to fetch screenshot files:', response.status);
          setScreenshotFiles([]);
          setScreenshotsLoading(false);
          return;
        }

        const data = await response.json();
        const pageFiles = data.data?.files || [];
        setScreenshotFiles(pageFiles);
      } catch (err) {
        console.error('[PlansSection] Error fetching screenshot files:', err);
        setScreenshotFiles([]);
      } finally {
        setScreenshotsLoading(false);
      }
    };

    fetchScreenshotFiles();
  }, [slug]);

  // Handler for running extract from ErrorState
  const handleRunExtract = async () => {
    if (!slug) return;

    try {
      // Get latest successful scrape version to use as period
      let period: string | null = null;

      const { data: scrapes, error: scrapeError } = await supabase
        .from('scrapes')
        .select('version, verified')
        .eq('slug', slug)
        .eq('verified', true)
        .order('version', { ascending: false })
        .limit(1);

      if (scrapeError) {
        console.error('Error fetching latest scrape:', scrapeError);
      } else if (scrapes && scrapes.length > 0) {
        period = scrapes[0].version;
      }

      const params: { slug: string; period?: string } = { slug };

      // Add period if we found a latest successful scrape
      if (period) {
        params.period = period;
      }

      const { error: insertError } = await supabase
        .from('claude_tasks')
        .insert({
          command: 'run_extract',
          slug: slug,
          params,
          status: 'pending',
          priority: 5,
        });

      if (insertError) {
        throw insertError;
      }

      toast({
        title: 'Extract task created',
        description: `Extraction queued for "${slug}"${period ? ` (period: ${period})` : ''}`,
      });
    } catch (err) {
      console.error('Error creating extract task:', err);
      toast({
        title: 'Error creating task',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    }
  };

  // Handler for opening screenshot in new tab
  const handleOpenScreenshot = () => {
    if (!currentPage?.version) return;

    // Find the screenshot file matching the current version (YYYYMMDD format)
    const matchingFile = screenshotFiles.find(file => file.version === currentPage.version);

    if (matchingFile?.screenshot_url) {
      window.open(matchingFile.screenshot_url, '_blank');
    } else {
      toast({
        title: 'Screenshot not available',
        description: `No screenshot found for version ${currentPage.version}`,
        variant: 'destructive',
      });
    }
  };

  const currentPage = data?.pages[selectedPageIndex];

  // Determine the appropriate error to display based on scrape availability
  const displayError = error && !loading && !screenshotsLoading ? (() => {
    // If it's a NOT_FOUND or NETWORK_ERROR (500), check if screenshots exist
    if ((error.type === 'NOT_FOUND' || error.type === 'NETWORK_ERROR') && screenshotFiles.length === 0) {
      return {
        type: 'NO_SCRAPES' as const,
        message: 'Please scrape this pricing page first. Screenshots are required before pricing data can be extracted.',
        status: error.status
      };
    }
    // For NOT_FOUND with existing screenshots, this is not an error - just empty state
    // Return null so the friendly empty state is shown instead
    if (error.type === 'NOT_FOUND' && screenshotFiles.length > 0) {
      return null;
    }
    // For actual errors (AUTH_ERROR, PARSE_ERROR, real NETWORK_ERROR), show error
    return error;
  })() : error;

  return (
    <div className="space-y-4">
      {/* Header with page selector and extract button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Pricing Plans
          </span>
          {currentPage?.version && (
            <>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="text-gray-400 hover:text-gray-600 transition-colors">
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="max-w-xs p-3 text-sm bg-gray-900 text-white border-gray-700"
                  >
                    <div className="whitespace-pre-line font-medium">
                      {formatVersionInfo(currentPage.version)}
                    </div>
                    {currentPage.scrape?.url && (
                      <a
                        href={currentPage.scrape.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block mt-2 text-blue-400 hover:text-blue-300 text-xs"
                      >
                        View source page →
                      </a>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleOpenScreenshot}
                      className="text-blue-500 hover:text-blue-700 transition-colors"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View screenshot</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Version Selector */}
          {versions.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-1.5" disabled={versionsLoading}>
                  <Calendar className="h-3.5 w-3.5" />
                  <span className="text-xs">
                    {selectedVersion
                      ? formatVersionLabel(versions.find(v => v.version === selectedVersion) || { version: selectedVersion })
                      : 'Latest'}
                  </span>
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-64 overflow-y-auto">
                <DropdownMenuItem
                  onClick={() => setSelectedVersion(null)}
                  className={!selectedVersion ? 'bg-blue-50 text-blue-700' : ''}
                >
                  Latest
                </DropdownMenuItem>
                {versions.map((v) => (
                  <DropdownMenuItem
                    key={v.version}
                    onClick={() => setSelectedVersion(v.version)}
                    className={selectedVersion === v.version ? 'bg-blue-50 text-blue-700' : ''}
                  >
                    {formatVersionLabel(v)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {data?.pages && data.pages.length > 1 && (
            <PageSelector
              pages={data.pages}
              currentIndex={selectedPageIndex}
              onChange={setSelectedPageIndex}
            />
          )}
          {currentPage && <JsonViewerModal data={currentPage} title="Raw Page Data" />}
          <RunExtractButton slug={slug} selectedVersion={selectedVersion} />
        </div>
      </div>

      {/* Loading State */}
      {(loading || screenshotsLoading) && (
        <div className="space-y-4">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-600">Loading pricing data...</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48 w-full rounded-lg" />
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {!loading && !screenshotsLoading && displayError && (
        <ErrorState error={displayError} onRetry={refetch} onRunExtract={handleRunExtract} />
      )}

      {/* Content */}
      {!loading && !screenshotsLoading && !displayError && data && currentPage && (
        <div className="space-y-4">
          {/* Plans Display */}
          {currentPage.plans && currentPage.plans.length > 0 ? (
            <PricingCardsDisplay
              plans={currentPage.plans}
              pageMetrics={currentPage.pricing_metric}
            />
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-gray-400 text-sm mb-3">No pricing plans available</div>
              <RunExtractButton slug={slug} variant="default" size="sm" />
            </div>
          )}

          {/* Page Metadata */}
          {currentPage.version && (
            <div className="text-center text-xs text-gray-500">
              <span>Extracted: </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help underline decoration-dotted">
                      {currentPage.version}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{formatVersionInfo(currentPage.version)}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {currentPage.scrape?.url && (
                <>
                  {' · '}
                  <a
                    href={currentPage.scrape.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    View Source
                  </a>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* No Data State - Screenshots exist but pricing not extracted yet */}
      {!loading && !screenshotsLoading && !displayError && !data && (
        <div className="text-center py-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
          <div className="mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-3">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Ready to Extract Pricing Data</h3>
            <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
              {screenshotFiles.length > 0
                ? `${screenshotFiles.length} screenshot${screenshotFiles.length > 1 ? 's' : ''} available. Click below to extract pricing information.`
                : 'Click below to extract pricing information from this page.'
              }
            </p>
          </div>
          <RunExtractButton slug={slug} variant="default" size="default" />
        </div>
      )}
    </div>
  );
};

export default PlansSection;
